/**
 * Identifier generation and validation.
 *
 * This file exists because the three identifiers in this app are NOT
 * interchangeable and confusing any two of them produces a failure that reads
 * like something else:
 *
 *   session_id   ours, and authoritative. Ruling R3: the caller's id becomes
 *                both the LiveKit room name and the control plane's primary
 *                key, so there is never a second identifier to translate
 *                between. We mint it; the API stores it.
 *   identity     ours, per participant, and STRICTLY VALIDATED upstream. Not
 *                a name. Never derived from a name.
 *   join_code    ours alone. The control plane has no model for it. It exists
 *                so a human can read six characters down a phone line.
 *
 * Everything here is pure and isomorphic: `crypto.getRandomValues` is a global
 * in Node 22 and in every browser we target, so the same code runs in Fastify
 * and in React with no polyfill and no `node:crypto` import.
 */
/* -------------------------------------------------------------------------- */
/* identity                                                                    */
/* -------------------------------------------------------------------------- */
/**
 * The API's own rule, from ParticipantSpec.identity in the spec:
 *
 *   "Opaque, non-PII, dot-free. This platform never interprets it. It lands in
 *    B2 object keys, which cannot be rewritten on an erasure request, and
 *    LiveKit strips extension-like suffixes from it (`output_file.go:142-147`)
 *    so a dot silently collides two participants into one key."
 *
 * So a display name is not merely discouraged as an identity — "Ana Ruiz" is
 * rejected with 422, and "bob.smith" would be accepted and then quietly
 * truncated to "bob" inside an object key that can never be corrected.
 *
 * The mapping is therefore: mint `u_` + random, send that, and keep the human
 * name in SQLite beside it (`participant.display_name`). Never send a display
 * name as an identity, and never try to sanitise one into an identity — that
 * makes two humans with the same name collide.
 */
const IDENTITY_ALPHABET = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
/** `u_` + 10 random chars. ~57^10 ≈ 2^58 of space, in a 12-char string. */
export function newIdentity() {
    return `u_${randomString(IDENTITY_ALPHABET, 10)}`;
}
/** Max length the API accepts (ParticipantSpec.identity.maxLength). */
export const IDENTITY_MAX_LENGTH = 128;
/**
 * Mirrors what the control plane will accept, so a bad identity fails here
 * with a sentence a developer can read instead of as a 422 from three
 * network hops away.
 *
 * Deliberately stricter than the spec's stated rules: the spec forbids dots
 * and PII, we forbid everything that is not `[A-Za-z0-9_-]`. Anything outside
 * that set ends up percent-encoded in a URL path or an object key somewhere,
 * and this app never needs the extra characters.
 */
export function isValidIdentity(value) {
    return value.length > 0 && value.length <= IDENTITY_MAX_LENGTH && /^[A-Za-z0-9_-]+$/.test(value);
}
/* -------------------------------------------------------------------------- */
/* session id                                                                  */
/* -------------------------------------------------------------------------- */
/**
 * `s_` + a lexically sortable time prefix + randomness.
 *
 * The time prefix is not decoration: session ids show up in B2 object keys and
 * in operator listings, and an id that sorts by creation time makes a prefix
 * listing readable without a join. It is NOT a source of truth for creation
 * time — `room.created_at` is.
 *
 * `maxLength` upstream is 128; this is 30.
 */
export function newSessionId(now = new Date()) {
    const stamp = now.toISOString().replace(/[-:TZ.]/g, '').slice(0, 14); // YYYYMMDDhhmmss
    return `s_${stamp}_${randomString(IDENTITY_ALPHABET, 10)}`;
}
export const SESSION_ID_MAX_LENGTH = 128;
export function isValidSessionId(value) {
    return value.length > 0 && value.length <= SESSION_ID_MAX_LENGTH && /^[A-Za-z0-9_-]+$/.test(value);
}
/* -------------------------------------------------------------------------- */
/* join code                                                                   */
/* -------------------------------------------------------------------------- */
/**
 * The alphabet a human reads aloud.
 *
 * Removed: 0/O, 1/I/L, and U. The first two pairs are the classic misreads in
 * every sans-serif UI font; U is dropped because Crockford drops it and
 * because it lets the set avoid spelling short words by accident.
 *
 * 30 symbols, 6 characters => 729 million codes. Collisions are handled by the
 * UNIQUE constraint on `room.join_code` and a retry, not by hoping.
 */
export const JOIN_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';
export const JOIN_CODE_LENGTH = 6;
/** Canonical form is upper case and ungrouped: `K7M2QX`. */
export function newJoinCode() {
    return randomString(JOIN_CODE_ALPHABET, JOIN_CODE_LENGTH);
}
/**
 * Accepts what a human actually types and returns the canonical code, or null.
 *
 * Handles the three things people do to a code they were read over the phone:
 * lower case it, put a space or a dash in the middle, and paste it with
 * surrounding whitespace.
 *
 * There is deliberately NO character substitution table here. The usual
 * `O -> 0, I -> 1` rescue is impossible for this alphabet, because 0 and 1 are
 * not in it — there is nothing to map them to. That is the whole reason the
 * alphabet was chosen this way: normalisation reduces to "strip separators,
 * upper case", with no lookup table to get wrong later.
 */
export function normalizeJoinCode(input) {
    const cleaned = input.trim().toUpperCase().replace(/[\s-]+/g, '');
    if (cleaned.length !== JOIN_CODE_LENGTH)
        return null;
    for (const character of cleaned) {
        if (!JOIN_CODE_ALPHABET.includes(character))
            return null;
    }
    return cleaned;
}
/** `K7M-2QX` — for display only. Never store or compare the formatted form. */
export function formatJoinCode(code) {
    return code.length === 6 ? `${code.slice(0, 3)}-${code.slice(3)}` : code;
}
/* -------------------------------------------------------------------------- */
/* appointment join slug                                                       */
/* -------------------------------------------------------------------------- */
/**
 * The stable, shareable address of a MEETING, as opposed to a join code, which
 * addresses one disposable media session.
 *
 * A fourth identifier needs justifying, so: a join code cannot do this job.
 * A code is minted with a room, and under the host-gated flow no room exists
 * when the link has to be sent. The link must also survive every occurrence,
 * while a code dies with its session. They are different lifetimes, so they
 * are different identifiers.
 *
 * Longer than a join code (10 chars, ~2^49) because it is pasted rather than
 * read aloud, and because it is the ONLY thing standing between a stranger and
 * a lobby. Guessing one still yields nothing but a waiting screen — starting
 * the meeting needs the host capability — but the arithmetic should not be the
 * interesting part of that sentence.
 */
export const JOIN_SLUG_LENGTH = 10;
export function newJoinSlug() {
    return randomString(JOIN_CODE_ALPHABET, JOIN_SLUG_LENGTH);
}
/** Same forgiving normalisation as a join code, at slug length. */
export function normalizeJoinSlug(input) {
    const cleaned = input.trim().toUpperCase().replace(/[\s-]+/g, '');
    if (cleaned.length !== JOIN_SLUG_LENGTH)
        return null;
    for (const character of cleaned) {
        if (!JOIN_CODE_ALPHABET.includes(character))
            return null;
    }
    return cleaned;
}
/* -------------------------------------------------------------------------- */
/**
 * Rejection sampling, not `% alphabet.length`.
 *
 * The modulo version biases the first `256 % 30 = 16` symbols upward by about
 * 3.5%. It does not matter for a join code and it matters a great deal for an
 * identity, and having two generators where one is subtly weaker is how the
 * weak one ends up in the wrong place.
 */
function randomString(alphabet, length) {
    const limit = Math.floor(256 / alphabet.length) * alphabet.length;
    const out = [];
    const buffer = new Uint8Array(length * 2);
    while (out.length < length) {
        crypto.getRandomValues(buffer);
        for (const byte of buffer) {
            if (byte >= limit)
                continue;
            out.push(alphabet[byte % alphabet.length]);
            if (out.length === length)
                break;
        }
    }
    return out.join('');
}
//# sourceMappingURL=ids.js.map